import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import SimpleButton from '../common/SimpleButton'
import SearchWithFilter from '../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import CycleDetailsModal from '../common/CycleDetailsModal'
import Dropdown from '../common/Dropdown'
import ResponsiveTable from '../common/ResponsiveTable'
import { toast } from '../common/Toast'
import { useWindowSize } from '../../libs/hooks'
import { convertToSimpleDateWithTime } from '../../utils/filters/date'
import {
  getProductCyclesFaultsBySn,
  downloadCycleFaultData,
  getUniqueCfBySn,
  getProductCyclePrintout,
  downloadCycleFaultPrintoutData,
} from '../../utils/requests/productsAPI'
import { downloadFile } from '../../utils/functions'
import { DownloadIcon } from '../../icons'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import './cycle-faults.scss'

const CycleFaults = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { serialNumber, model } = props
  const [cfTableData, setCfTableData] = useState([])
  const [showSpinner, setShowSpinner] = useState(null)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedCycleFault, setSelectedCycleFault] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [cycleFaultList, setCycleFaultList] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [startDateFilter, setStartDateFilter] = useState(null)
  const [endDateFilter, setEndDateFilter] = useState(null)
  const [cycleDetails, setCycleDetails] = useState({})
  const [cycleDetailsDeviceData, setCycleDetailsDeviceData] = useState({})
  const [orderBy, setOrderBy] = useState(null)
  const hasData = cfTableData && cfTableData.length > 0

  const tableHeaders = [
    {
      field: 'cycle_date',
      translation: t('products.notes.cycle-date'),
      hasOrdering: true,
      onOrderAscending: () => handleSort('cycle_date', 'asc'),
      onOrderDescending: () => handleSort('cycle_date', 'desc'),
    },
    {
      field: 'cycle_number',
      translation: `${t('device_context.cycle_number')}`,
      hasOrdering: true,
      onOrderAscending: () => handleSort('cycle_number', 'asc'),
      onOrderDescending: () => handleSort('cycle_number', 'desc'),
    },
    {
      field: 'cycle_fault',
      translation: t('cycle_fault'),
      hasOrdering: false,
    },
    {
      field: 'printout_file_name',
      translation: t('printout_file_name'),
      hasOrdering: false,
    },
    {
      field: 'files',
      translation: t('files'),
      hasOrdering: false,
    },
  ]

  const orderingFields = [
    {
      field: 'cycle_date',
      order: 'asc',
      translationKey: 'cycle_date_asc',
    },
    {
      field: 'cycle_date',
      order: 'desc',
      translationKey: 'cycle_date_desc',
    },
    {
      field: 'cycle_number',
      order: 'asc',
      translationKey: 'cycle_number_asc',
    },
    {
      field: 'cycle_number',
      order: 'desc',
      translationKey: 'cycle_number_desc',
    },
  ]

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(() => {
      if (shouldApplyFilters) {
        setSelectedPage(1)
      }

      setShouldApplyFilters(true)
    }, 0)
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [selectedCycleFault])

  useEffect(() => {
    if (serialNumber) {
      setShowSpinner(true)

      const params = {
        page: selectedPage,
        limit: 20,
      }
      if (selectedCycleFault) params.cf_code = selectedCycleFault
      if (startDateFilter) params.start_date = startDateFilter
      if (endDateFilter) params.end_date = endDateFilter
      if (orderBy) {
        params.order_by = orderBy.headerField
        params.order = orderBy.order
      }

      getProductCyclesFaultsBySn(serialNumber, params)
        .then((data) => {
          if (data?.data) {
            const tableData = data.data.map((item, index) => {
              return {
                identifier: item.cfID,
                cycle_number: (
                  <span
                    className="cpt-txt"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpenViewModal(item)
                    }}
                  >
                    {item?.cycleNumber ?? ''}
                  </span>
                ),
                cycle_fault: item.cycleFault,
                cycle_date: convertToSimpleDateWithTime(item.dateTime),
                sn: item.sn,
                printout_file_name: item.printoutFileName,
                files:
                  (item?.printoutFileName && item.printoutFileName.slice(0, 1) === 'B') || (model && model.toLowerCase().includes('bravo')) ? (
                    <span
                      className={item?.jsonExist ? 'cpt-txt' : 'cpt-txt-disabled'}
                      onClick={(e) => {
                        e.stopPropagation()
                        item?.jsonExist && handlePrintoutDownload(item.printoutFileName, 'JSON')
                      }}
                    >
                      <DownloadIcon className="download-icon" />
                      JSON
                    </span>
                  ) : (
                    <div>
                      <span
                        className={item?.cptExist ? 'cpt-txt' : 'cpt-txt-disabled'}
                        onClick={(e) => {
                          e.stopPropagation()
                          item?.cptExist && handlePrintoutDownload(item.printoutFileName, 'CPT')
                        }}
                      >
                        <DownloadIcon className="download-icon" />
                        CPT
                      </span>
                      <span
                        className={item?.txtExist ? 'cpt-txt' : 'cpt-txt-disabled'}
                        onClick={(e) => {
                          e.stopPropagation()
                          item?.txtExist && handlePrintoutDownload(item.printoutFileName, 'TXT')
                        }}
                      >
                        <DownloadIcon className="download-icon" />
                        TXT
                      </span>
                    </div>
                  ),
              }
            })
            setCfTableData((prev) => (selectedPage === 1 ? tableData : [...prev, ...tableData]))
            setCanLoadMore(data.nextPage)
          }
        })
        .catch(() => {
          toast.error(t('error.failure_msg'))
        })
        .finally(() => {
          setShowSpinner(false)
          setIsFirstLoad(false)
        })

      return
    }

    setIsFirstLoad(false)
  }, [serialNumber, selectedPage, selectedCycleFault, startDateFilter, endDateFilter, orderBy])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(() => {
      if (shouldApplyFilters) {
        setSelectedPage(1)
      }

      setShouldApplyFilters(true)
    })
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [selectedCycleFault, startDateFilter, endDateFilter, orderBy])

  useEffect(() => {
    if (serialNumber) {
      getUniqueCfBySn(serialNumber)
        .then((result) => {
          if (result) {
            const listData = result.map((item, index) => ({ id: index + 1, fault: item }))
            setCycleFaultList(listData)
          }
        })
        .catch(() => {
          toast.error(t('error.failure_msg'))
        })
      return
    }

    setIsFirstLoad(false)
  }, [serialNumber])

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setStartDateFilter(null)
    setEndDateFilter(null)
    setSelectedPage(1)
    setSelectedCycleFault(null)
  }

  const handleOpenViewModal = (row) => {
    setShowSpinner(true)
    getProductCyclePrintout(row?.printoutFileName)
      .then((res) => {
        const data = res.data
        if (!data || !data.serial_number || !data.model) {
          toast.error(t('no_data_found'))
          return
        }

        setCycleDetails(data)
        setCycleDetailsDeviceData({
          model,
          cycleNumber: row?.cycleNumber,
          cycleStartTime: row?.dateTime,
          deviceSerialNumber: row?.sn,
        })
        setShowViewModal(true)
      })
      .catch((error) => {
        toast.error(t(`error.failure_msg`))
      })
      .finally(() => {
        setShowSpinner(false)
      })
  }

  const handleDownload = () => {
    const params = {}
    if (selectedCycleFault) params.cf_code = selectedCycleFault
    downloadCycleFaultData(serialNumber, params)
      .then((res) => {
        const date = new Date().toISOString().split('T')
        const dateNow = date[0].split('-').reverse().join('_')
        const timeNow = date[1].split('.')[0].split(':').join('_')

        downloadFile({
          data: res,
          fileName: `SN-${serialNumber}_Cycle-faults_${dateNow}_${timeNow}.csv`,
          fileType: 'text/csv',
        })
      })
      .catch(() => {
        toast.error(t('error.failure_msg'))
      })
  }

  const keyValueCfList = cycleFaultList?.map((cycle) => ({ key: cycle.id, value: cycle.fault }))

  const handlePrintoutDownload = (fileName, fileType) => {
    const params = {
      printout_filename: `${fileName}.${fileType.toLowerCase()}`,
    }
    downloadCycleFaultPrintoutData(serialNumber, params)
      .then((res) => {
        const date = new Date().toISOString().split('T')
        const dateNow = date[0].split('-').reverse().join('_')
        const timeNow = date[1].split('.')[0].split(':').join('_')

        let processedData = res
        let mimeType = 'application/octet-stream'

        switch (fileType?.toLowerCase()) {
          case 'json':
            if (typeof res === 'object') {
              processedData = JSON.stringify(res, null, 2)
            }
            mimeType = 'application/json'
            break

          case 'txt':
            processedData = typeof res === 'string' ? res : JSON.stringify(res, null, 2)
            mimeType = 'text/plain;charset=utf-8'
            break

          case 'cpt':
            processedData = typeof res === 'string' ? res : JSON.stringify(res, null, 2)
            mimeType = 'text/plain;charset=utf-8'
            break

          default:
            processedData = res
            break
        }

        downloadFile({
          data: processedData,
          fileName: `${fileName}_${dateNow}_${timeNow}.${fileType.toLowerCase()}`,
          fileType: mimeType,
        })
      })
      .catch((error) => {
        console.error('Download error:', error)
        toast.error(t('error.failure_msg'))
      })
  }

  const handleMobileSort = (orderingField) => {
    const { field, order } = orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order)
    setOrderBy({ headerField: field, order })
    setSelectedPage(1)
  }

  const handleSort = (field, order) => {
    setOrderBy({ headerField: field, order })
    setSelectedPage(1)
  }

  return (
    <div className="cycle-faults-tab-wrapper">
      <div className="page-section">
        <div className="actions-wrapper">
          <div className="filter-position">
            <SearchWithFilter
              showFiltersModal={() => setShowFiltersModal(true)}
              onResetFilters={() => handleResetFilters()}
              showCounterBadge={(startDateFilter && endDateFilter) || selectedCycleFault}
              counterBadge={(startDateFilter && endDateFilter ? 1 : 0) + (selectedCycleFault ? 1 : 0)}
            />
          </div>
          <div className="table-actions-row">
            <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isTableLoading || !hasData}>
              <DownloadIcon />
              {t('download')}
            </SimpleButton>
          </div>
        </div>
      </div>
      {!isMobile ? (
        cfTableData?.length > 0 && (
          <ResponsiveTable className="cycle-faults-table" headers={tableHeaders} data={cfTableData} expandingColumn={4} activeSort={orderBy} />
        )
      ) : (
        <div className="mobile-content">
          {cfTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleMobileSort(sortOption)
                }}
              />
            </div>
          )}
          {cfTableData.map((item, index) => (
            <div key={index} className="card">
              <div className="card-item">
                <div className="card-item-title">{t('serial_number')}</div>
                <div className="card-item-body">{item.sn}</div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{`${t('device_context.cycle_number')}`}</div>
                <div className="card-item-body cycle-number">{item.cycle_number}</div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{t('cycle_fault')}</div>
                <div className="card-item-body cycle-fault">{item.cycle_fault}</div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{t('products.notes.cycle-date')}</div>
                <div className="card-item-body">{item.cycle_date}</div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{t('printout_file_name')}</div>
                <div className="card-item-body">{item.printout_file_name}</div>
              </div>
              <div className="card-item">
                <div className="card-item-title">{t('files')}</div>
                <div className="card-item-body">{item.files}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showViewModal && (
        <CycleDetailsModal
          deviceData={cycleDetailsDeviceData}
          cycleDetails={cycleDetails}
          closeModal={() => {
            setShowViewModal(false)
            setCycleDetails({})
            setCycleDetailsDeviceData({})
          }}
        />
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('cf_codes'),
              type: FilterType.singleSelect,
              dataset: keyValueCfList.map((code) => code.value),
              input: selectedCycleFault,
              output: (code) => setSelectedCycleFault(code),
            },
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('1_month'), months: 1 },
                { label: t('3_months'), months: 3 },
                { label: t('6_months'), months: 6 },
                { label: t('12_months'), months: 12 },
              ],
            },
          ]}
          shouldFilter={(shouldApply) => {
            setSelectedPage(1)
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
      {!isFirstLoad && cfTableData.length === 0 && <EmptyPageResults className="empty-list" title={t('no_data_available')} />}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export default CycleFaults

